/**
 * @module wgl/WebGLBuffer
 * Module containing a helper class for WebGL data buffers
 */
const getGLDtype = (gl, ary) => {
    const DTYPES = {
        'Float32Array': gl.FLOAT,
        'Uint8Array': gl.UNSIGNED_BYTE,
        'Uint16Array': gl.UNSIGNED_SHORT,
        'Uint32Array': gl.UNSIGNED_INT,
    };
    return DTYPES[ary.constructor.name];
};
class WGLBufferBase {
    constructor(gl, verts, n_coords_per_vert, draw_mode, bind_target) {
        this.gl = gl;
        this.n_coords_per_vert = n_coords_per_vert;
        this.dtype = getGLDtype(gl, verts);
        this.n_verts = verts.length / n_coords_per_vert;
        this.draw_mode = draw_mode;
        const buffer = gl.createBuffer();
        if (buffer === null) {
            throw "Could not create WebGL buffer";
        }
        this.buffer = buffer;
        gl.bindBuffer(bind_target, this.buffer);
        gl.bufferData(bind_target, verts, gl.STATIC_DRAW);
    }
}
/** A class representing a WebGL data buffer */
class WGLBuffer extends WGLBufferBase {
    /**
     * Create a WebGL buffer and put some data in it
     * @param gl                 - The WebGL rendering context
     * @param verts              - The vertex data to use for this buffer
     * @param n_coords_per_vert  - The number of coordinates for each vertex in the data buffer
     * @param draw_mode          - The draw mode to use for this buffer. Should be one of gl.TRIANGLE_STRIP, etc.
     */
    constructor(gl, verts, n_coords_per_vert, draw_mode) {
        super(gl, verts, n_coords_per_vert, draw_mode, gl.ARRAY_BUFFER);
    }
    /**
     * Bind this buffer to a location in a shader program
     * @internal
     * @param prog_attr_location - The location of the variable in the shader program (returned from gl.getAttribLocation())
     */
    bindToProgram(prog_attr_location) {
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.buffer);
        this.gl.enableVertexAttribArray(prog_attr_location);
        this.gl.vertexAttribPointer(prog_attr_location, this.n_coords_per_vert, this.dtype, false, 0, 0);
    }
}
/** A class representing a WebGL data buffer for array indices */
class WGLIndexBuffer extends WGLBufferBase {
    /**
     *
     * @param gl         - The WebGL rendering context
     * @param indices    - The index data
     * @param draw_mode  - The draw mode to use for this buffer. Should be one of gl.TRIANGLE_STRIP, etc.
     */
    constructor(gl, indices, draw_mode) {
        super(gl, indices, 1, draw_mode, gl.ELEMENT_ARRAY_BUFFER);
    }
    /**
     * Bind this buffer to the ELEMENT_ARRAY_BUFFER target
     * @internal
     */
    bind() {
        this.gl.bindBuffer(this.gl.ELEMENT_ARRAY_BUFFER, this.buffer);
    }
}
export { WGLBuffer, WGLIndexBuffer };
